"use client";

import CompactTickerCard from "./CompactTickerCard";
import { useDashboard } from "@/modules";
import { formatWidgetDateRangeLabel, formatDate, getDateRangeForPreset } from "@/modules/widgets/utils/dateUtils";

type BudgetTickerItem = {
    title?: string;
    icon?: string;
    color?: string;
    hideActions?: boolean;
    hideInsights?: boolean;
    data?: {
        value: number;
        changeType: "positive" | "negative";
        visualizationType?: "chart" | "none";
        periods?: [{ label: string; value: number }, { label: string; value: number }];
        previousPeriodComparison?: {
            value: number;
            changeType: "positive" | "negative";
            percentageChange: string;
        };
    };
};

type Props = {
    tickers?: BudgetTickerItem[];
    widget?: any;
    onWidgetDelete?: () => void;
    hideActions?: boolean;
    hideInsights?: boolean;
};

export const BudgetTickersGroupByProduct = ({ tickers, widget, onWidgetDelete, hideActions, hideInsights }: Props) => {
    const items = Array.isArray(tickers) ? tickers : [];   
    const {
        state: { selectedDateRange },
    } = useDashboard();

    const widgetDatePeriod = widget?.datePeriod;
    const widgetStartDate = widget?.service?.parameters?.startDate;
    const widgetEndDate = widget?.service?.parameters?.endDate;

    const presetRange = widgetDatePeriod ? getDateRangeForPreset(widgetDatePeriod) : null;

    const widgetDateRangeLabel =
        widgetDatePeriod === "dashboard_date"
            ? null
            : !widgetDatePeriod || widgetDatePeriod === "custom"
                ? formatWidgetDateRangeLabel(widgetStartDate, widgetEndDate)
                : presetRange
                    ? formatWidgetDateRangeLabel(formatDate(presetRange!.from), formatDate(presetRange!.to))
                    : null;

    const isAIWidget = Boolean(widget?.prompt);
    const aiDateRangeLabel = isAIWidget ? (widgetDateRangeLabel || undefined) : undefined;

    if (items.length === 0) {
        return (
            <div className="w-full flex items-center justify-center h-[74px]">

            </div>
        );
    }

    // Single ticker view - render centered without grid
    if (items.length === 1) {
        const ticker = items[0];
        return (
            <div className="w-full flex justify-center">
                <div className="w-full max-w-md">
                    <CompactTickerCard
                        title={ticker.title}
                        dateRangeLabel={aiDateRangeLabel}
                        iconUrl={ticker.icon}
                        data={ticker.data as any}
                        widget={widget}
                        onWidgetDelete={onWidgetDelete}
                        colors={Array.isArray(ticker.color) ? ticker.color : (ticker.color ? [ticker.color] : widget?.colors)}
                        enableGradient={true}
                        disableTrend={true}
                        hideActions={hideActions}
                        hideInsights={hideInsights}
                        forceDecimals={0}
                    />
                </div>
            </div>
        );
    }

    const renderTicker = (t: BudgetTickerItem, idx: number) => (
        <div key={`${t.title ?? "ticker"}-${idx}`} className="min-w-0 overflow-hidden">
            <CompactTickerCard
                title={t.title}
                dateRangeLabel={aiDateRangeLabel}
                iconUrl={t.icon}
                data={t.data as any}
                widget={widget}
                onWidgetDelete={onWidgetDelete}
                colors={Array.isArray(t.color) ? t.color : (t.color ? [t.color] : widget?.colors)}
                enableGradient={true}
                disableTrend={true}
                hideActions={true}
                hideInsights={true}
                forceDecimals={0}
            />
        </div>
    );

    // 2-5 tickers: single row
    if (items.length <= 5) {
        return (
            <div className={`w-full grid gap-3 overflow-hidden`} style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
                {items.map(renderTicker)}
            </div>
        );
    }

    const buildGrid = (items: BudgetTickerItem[], maxCols = 5): BudgetTickerItem[][] => {
        const total = items.length;
        const rows = Math.ceil(total / maxCols);
        const baseSize = Math.floor(total / rows);
        let remainder = total % rows;
        const grid: BudgetTickerItem[][] = [];
        let index = 0;
        for (let r = 0; r < rows; r++) {
            const rowSize = baseSize + (remainder > 0 ? 1 : 0);
            remainder--;
            grid.push(items.slice(index, index + rowSize));
            index += rowSize;
        }
        return grid;
    };

    const grid = buildGrid(items);

    return (
        <div className="w-full flex flex-col gap-3 overflow-hidden">
            {grid.map((row, rowIdx) => (
                <div key={rowIdx} className="grid gap-3" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
                    {row.map((t, idx) => renderTicker(t, grid.slice(0, rowIdx).reduce((acc, r) => acc + r.length, 0) + idx))}
                </div>
            ))}
        </div>
    );
};

BudgetTickersGroupByProduct.displayName = "BudgetTickersGroupByProduct";